import { apiRequest } from './mvpApi';
import type { SupportedLocale } from '../config/localeConfig';
import type { SupportedMarket } from '../config/marketConfig';

export type LocalePublishState = Partial<
  Record<
    SupportedLocale,
    {
      isPublished?: boolean;
      isIndexable?: boolean;
      publishedAt?: string | null;
    }
  >
>;

export type PublicEntityResponse<TData> = {
  locale: SupportedLocale;
  market?: SupportedMarket;
  fallbackUsed: boolean;
  translationStatus?: string;
  isIndexable?: boolean;
  data: TData;
};

export type LocalizedSeo = {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  twitterTitle?: string;
  twitterDescription?: string;
};

export type PublicPackage = {
  id: string;
  baseSlug: string;
  slug: string;
  title: string;
  excerpt?: string;
  summary?: string;
  body?: string;
  highlights?: string[];
  inclusions?: string[];
  exclusions?: string[];
  seo?: LocalizedSeo;
  localePublishState?: LocalePublishState;
};

export type PublicDestination = {
  id: string;
  baseSlug: string;
  slug: string;
  title: string;
  name?: string;
  excerpt?: string;
  summary?: string;
  body?: string;
  seo?: LocalizedSeo;
  localePublishState?: LocalePublishState;
};

export type PublicArticle = {
  id: string;
  baseSlug: string;
  slug: string;
  title: string;
  excerpt?: string;
  body?: string;
  seo?: LocalizedSeo;
  publishedAt?: string;
  localePublishState?: LocalePublishState;
};

type ListResponse<TItem> = {
  locale: SupportedLocale;
  market?: SupportedMarket;
  items: TItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

export async function fetchPackageBySlug(args: {
  slug: string;
  locale: SupportedLocale;
  market?: SupportedMarket;
  signal?: AbortSignal;
}) {
  const { slug, locale, market, signal } = args;

  return apiRequest<PublicEntityResponse<PublicPackage>>(
    `/public/packages/${encodeURIComponent(slug)}`,
    {
      query: { locale, market },
      signal,
    }
  );
}

export async function fetchDestinationBySlug(args: {
  slug: string;
  locale: SupportedLocale;
  market?: SupportedMarket;
  signal?: AbortSignal;
}) {
  const { slug, locale, market, signal } = args;

  return apiRequest<PublicEntityResponse<PublicDestination>>(
    `/public/destinations/${encodeURIComponent(slug)}`,
    {
      query: { locale, market },
      signal,
    }
  );
}

export async function fetchArticleBySlug(args: {
  slug: string;
  locale: SupportedLocale;
  market?: SupportedMarket;
  signal?: AbortSignal;
}) {
  const { slug, locale, market, signal } = args;

  return apiRequest<PublicEntityResponse<PublicArticle>>(
    `/public/articles/${encodeURIComponent(slug)}`,
    {
      query: { locale, market },
      signal,
    }
  );
}

export async function fetchPackagesList(args: {
  locale: SupportedLocale;
  market?: SupportedMarket;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
}) {
  const { locale, market, page = 1, limit = 12, signal } = args;

  return apiRequest<ListResponse<PublicPackage>>('/public/packages', {
    query: { locale, market, page, limit },
    signal,
  });
}

export async function fetchDestinationsList(args: {
  locale: SupportedLocale;
  market?: SupportedMarket;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
}) {
  const { locale, market, page = 1, limit = 12, signal } = args;

  return apiRequest<ListResponse<PublicDestination>>('/public/destinations', {
    query: { locale, market, page, limit },
    signal,
  });
}

export async function fetchArticlesList(args: {
  locale: SupportedLocale;
  market?: SupportedMarket;
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  tag?: string;
  signal?: AbortSignal;
}) {
  const { locale, market, page = 1, limit = 12, search, category, tag, signal } = args;

  return apiRequest<ListResponse<PublicArticle>>('/public/articles', {
    query: { locale, market, page, limit, search, category, tag },
    signal,
  });
}
